/**
 * Админка карт `/mapsadmin`: управление интерактивными гиперболическими картами.
 *
 * По образцу `admin.html`: страница доступна только после HTTP Basic-авторизации
 * (Требование 1.1). До успешного входа инструменты управления скрыты, показывается
 * модалка входа. При наличии действующей сессии отображается список всех карт с
 * названием, типом геометрии, масштабом и статусом видимости (Требование 1.2),
 * а также действия: создание (Требование 1.3), редактирование конфигурации —
 * название, выбор фундаментального многоугольника/пресета (включая «крендель
 * рода 2», Требование 2.1), схема склейки, масштаб, цвет фона (Требование 1.8),
 * видимость (Требование 1.4), удаление (Требование 1.5) и переключение видимости
 * (Требование 1.6).
 *
 * Архитектура «тонкий DOM, чистое ядро» (как в {@link MapPage}): вся логика
 * преобразования данных формы в {@link MapConfig}, обратного маппинга и разбора
 * ответов сервера вынесена в чистые, покрытые юнит-тестами функции
 * ({@link buildMapConfigFromForm}, {@link mapConfigToFormValues},
 * {@link parseMapsList}, {@link formatGeometryType}). DOM-слой лишь читает
 * значения из полей, вызывает эти функции и отображает результат. Авторизация
 * делегирована {@link AdminSession} (общий с экраном карты механизм Basic-auth).
 *
 * Требования: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 1.8
 */

import type {
  FundamentalPolygon,
  GeometryType,
  MapConfig,
  SidePairingSpec,
} from '@langton/shared';
import {
  buildRegularPolygon,
  detectGeometryType,
  validateMapGeometry,
  MIN_GEOMETRY_SIDES,
  MAX_GEOMETRY_SIDES,
} from '@langton/shared';
import { AdminSession, type CredentialStorage } from './map-auth.js';
import { LegendEditor } from './legend-editor.js';

// ---------------------------------------------------------------------------
// Чистое ядро (без DOM) — покрывается юнит-тестами
// ---------------------------------------------------------------------------

/** Максимально допустимый масштаб карты (число уровней LOD). */
export const MAX_MAP_SCALE = 12;

/**
 * Границы и значение по умолчанию глубины прорисовки копий (Требование 12).
 * Целое число в диапазоне `[1, 12]`; по умолчанию `8` — совпадает с текущим
 * `neighborDepth` менеджера LOD, поэтому поведение существующих карт не меняется
 * (Требования 12.1, 12.6).
 */
export const MIN_COPY_DEPTH = 1;
export const MAX_COPY_DEPTH = 12;
export const DEFAULT_COPY_DEPTH = 8;

/**
 * Пресет фундаментального многоугольника, доступный для выбора в форме
 * (Требование 2.1). `custom` (не входит в список) означает ручной ввод `{p,q}`.
 */
export interface MapPreset {
  /** Ключ пресета (сохраняется в `polygon.preset`). */
  id: string;
  /** Человекочитаемое название для выпадающего списка. */
  label: string;
  /** Число сторон многоугольника `p`. */
  sides: number;
  /** Степень вершины `q` (число многоугольников, сходящихся в вершине). */
  vertexDegree: number;
  /** Слово склейки по умолчанию. */
  pairingWord: string;
}

/** Идентификатор режима ручного ввода геометрии (не пресет). */
export const CUSTOM_PRESET_ID = 'custom';

/**
 * Доступные пресеты фундаментальных многоугольников.
 *
 * Гиперболические пресеты (Требование 2.1): «крендель рода 2» (правильный
 * восьмиугольник `{8,8}` со склейкой `abABcdCD`) и «род 3» (`{12,12}`). Для
 * рода `g` берётся `4g`-угольник `{4g, 4g}`, у которого сумма внутренних углов
 * равна `2π` (корректное замощение вокруг вершины).
 *
 * Плоские (евклидовы) пресеты (Требования 3.1, 3.2): квадрат `{4,4}`, где
 * показатель кривизны `s = 1/4 + 1/4 = 1/2` — это ровно евклидова граница.
 * «Плоский тор» использует ориентируемое слово склейки `abAB`, «бутылка
 * Клейна» — неориентируемое `abAb` (пара `x…x` обращает ориентацию).
 *
 * Сферический пресет (Требование 3.3): квадрат `{4,3}`, где `s = 1/4 + 1/3 =
 * 7/12 > 1/2` — сферическая геометрия. ВНИМАНИЕ: `vertexDegree = 3` (не 4),
 * иначе `{4,4}` дало бы `s = 1/2` и евклидову геометрию. Слово склейки `abBA`
 * (a·b·b⁻¹·a⁻¹) ориентируемо и топологически задаёт сферу (род 0).
 *
 * Обратный маппинг {@link mapConfigToFormValues} восстанавливает `q` из суммы
 * углов (`q = round(sides · 2π / angleSum)`): тор/Клейн `{4,4}` → `q = 4`;
 * сфера `{4,3}` → `round(4 · 2π / (8π/3)) = 3`. Выбор пресета в списке
 * восстанавливается по `polygon.preset`.
 */
export const MAP_PRESETS: MapPreset[] = [
  {
    id: 'genus2',
    label: 'Крендель рода 2 — восьмиугольник {8,8}, abABcdCD',
    sides: 8,
    vertexDegree: 8,
    pairingWord: 'abABcdCD',
  },
  {
    id: 'genus3',
    label: 'Род 3 — двенадцатиугольник {12,12}',
    sides: 12,
    vertexDegree: 12,
    pairingWord: 'abcdefABCDEF',
  },
  // Плоские (евклидовы) пресеты — квадрат {4,4}, s = 1/2 (Требования 3.1, 3.2).
  {
    id: 'torus',
    label: 'Плоский тор — квадрат {4,4}, abAB',
    sides: 4,
    vertexDegree: 4,
    pairingWord: 'abAB',
  },
  {
    id: 'klein',
    label: 'Бутылка Клейна — квадрат {4,4}, abAb',
    sides: 4,
    vertexDegree: 4,
    pairingWord: 'abAb',
  },
  // Сферический пресет — квадрат {4,3}, s = 7/12 > 1/2 (Требование 3.3).
  // ВНИМАНИЕ: vertexDegree = 3 (не 4), иначе {4,4} даст евклидову геометрию.
  {
    id: 'sphere',
    label: 'Сфера — квадрат {4,3}, abBA',
    sides: 4,
    vertexDegree: 3,
    pairingWord: 'abBA',
  },
];

/**
 * Сырые значения формы создания/редактирования карты. Все поля уже приведены к
 * нужным типам DOM-слоем (числа — числа, чекбокс — boolean).
 */
export interface MapFormValues {
  /** Название карты. */
  name: string;
  /** Идентификатор выбранного пресета либо {@link CUSTOM_PRESET_ID}. */
  preset: string;
  /** Число сторон `p` (используется в режиме `custom`). */
  sides: number;
  /** Степень вершины `q` (используется в режиме `custom`). */
  vertexDegree: number;
  /** Слово склейки сторон (например, `abcdABCD`). */
  pairingWord: string;
  /** Масштаб карты (число уровней LOD). */
  scale: number;
  /** Начальный цвет фона поверхности (HEX). */
  backgroundColor: string;
  /** Видима ли карта в галерее. */
  visible: boolean;
  /**
   * Множитель максимального отдаления (Требования 2.1, 9.1): во сколько раз
   * область обзора при предельном отдалении может превышать одну фундаментальную
   * область. Допустимый диапазон [0.1, 100.0] с шагом 0.1; значение по умолчанию
   * 1.0. Значения меньше 1.0 показывают при предельном отдалении лишь фрагмент
   * фундаментальной области.
   */
  maxZoomOut: number;
  /**
   * Админский множитель максимального отдаления (Требование 11): предел
   * отдаления, действующий только когда пользователь авторизован как
   * администратор. Допустимый диапазон [0.1, 100.0] с шагом 0.1, значение по
   * умолчанию 1.0. По требованию не должен быть строже публичного предела
   * (`adminMaxZoomOut ≥ maxZoomOut`, Требование 11.3), чтобы администратор мог
   * отдалиться не ближе обычного зрителя.
   */
  adminMaxZoomOut: number;
  /**
   * Глубина прорисовки копий фундаментальной области (Требование 12): целое
   * число в диапазоне [{@link MIN_COPY_DEPTH}, {@link MAX_COPY_DEPTH}] (`[1, 12]`),
   * значение по умолчанию {@link DEFAULT_COPY_DEPTH} (`8`). Задаёт глубину
   * BFS-перечисления соседних плиток (`neighborDepth` в `LodManager`); жёсткий
   * потолок `MAX_VISIBLE_COPIES` сохраняется как страховка (Требование 12.5).
   */
  copyDepth: number;
  /**
   * Обход копий «вершинным кольцом» вместо BFS по длине слова: вокруг каждой
   * вершины многоугольника прорисовывается полный цикл примыкающих копий (в том
   * числе с противоположной от текущей области стороны вершины). По умолчанию
   * `false` (историческое поведение). Необязательно.
   */
  vertexRingTraversal?: boolean;
  /**
   * Скрыть визуально границу окружности Пуанкаре (внешнюю окружность диска).
   * По умолчанию `false` (граница видима). Необязательно.
   */
  hideDiskBoundary?: boolean;
  /**
   * Ссылка на превью-изображение карты (URL) для галереи (Требование 9.1, 9.4).
   * Пустая строка или отсутствие означает удаление превью — в галерее будет
   * показан статичный рендер фундаментальной области (Требование 9.7).
   */
  previewImageUrl?: string;
}

/**
 * Результат {@link buildMapConfigFromForm}: либо готовая конфигурация карты
 * (без `id`), либо перечень понятных сообщений об ошибках.
 */
export interface BuildMapConfigResult {
  /** Готовая конфигурация (без `id`) или `null`, если есть ошибки. */
  config: Omit<MapConfig, 'id'> | null;
  /** Сообщения об ошибках валидации (пусто при успехе). */
  errors: string[];
}

/** Проверить строку на формат HEX-цвета (`#rgb` или `#rrggbb`). */
export function isHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Человекочитаемое название типа геометрии (для колонки списка, Требование 1.2).
 */
export function formatGeometryType(type: GeometryType): string {
  switch (type) {
    case 'hyperbolic':
      return 'Гиперболическая';
    case 'euclidean':
      return 'Евклидова';
    case 'spherical':
      return 'Сферическая';
    default:
      return String(type);
  }
}

/**
 * Построить конфигурацию карты из значений формы (чистая функция).
 *
 * Порядок:
 * 1. Проверить название, масштаб, цвет фона и множитель максимального отдаления.
 * 2. Определить параметры `{p,q}` и слово склейки из пресета либо ручного ввода.
 * 3. Автоопределить тип геометрии по `{p,q}` ({@link detectGeometryType} —
 *    единственный источник решения, Требования 1.4, 1.5). Для автоопределённых
 *    евклидовых/сферических карт выполнить русскоязычную пред-валидацию чётности
 *    `p` и диапазона `[MIN_GEOMETRY_SIDES, MAX_GEOMETRY_SIDES]` (Требования 4.1,
 *    4.2, 8.1).
 * 4. Построить правильный многоугольник общим построителем
 *    ({@link buildRegularPolygon}): ветвь геометрии выбирается автоопределением,
 *    гиперболический путь неизменен (Требования 2.3, 2.4, 2.5).
 * 5. Провести структурную валидацию пары «многоугольник + склейка»
 *    ({@link validateMapGeometry}) — те же проверки, что и на сервере
 *    (Требования 4.3, 4.5); итоговый тип геометрии взять из успешного результата
 *    `validation.geometryType` (Требование 1.5), а не повторной классификацией.
 *
 * Функция чистая: не мутирует `values` и лишь возвращает `{ config, errors }`,
 * поэтому DOM-слой сохраняет введённые значения полей при ошибке (Требование 4.4).
 *
 * @param values значения формы
 * @returns конфигурация без `id` при успехе либо перечень ошибок
 */
export function buildMapConfigFromForm(values: MapFormValues): BuildMapConfigResult {
  const errors: string[] = [];

  const name = (values.name ?? '').trim();
  if (name === '') {
    errors.push('Укажите название карты.');
  }

  const { scale } = values;
  if (!Number.isInteger(scale) || scale < 1 || scale > MAX_MAP_SCALE) {
    errors.push(`Масштаб должен быть целым числом от 1 до ${MAX_MAP_SCALE}.`);
  }

  const backgroundColor = (values.backgroundColor ?? '').trim();
  if (!isHexColor(backgroundColor)) {
    errors.push('Цвет фона должен быть в формате HEX, например #1a1a1a.');
  }

  // Максимальное отдаление (Требования 2.6, 9.1, 9.2): число в диапазоне
  // [0.1, 100.0]. Нижняя граница расширена до 0.1, чтобы при предельном отдалении
  // можно было видеть лишь фрагмент фундаментальной области. При выходе за
  // диапазон сохранение отклоняется, а значения формы сохраняются (функция лишь
  // возвращает ошибки, не закрывая модалку).
  const { maxZoomOut } = values;
  if (!Number.isFinite(maxZoomOut) || maxZoomOut < 0.1 || maxZoomOut > 100) {
    errors.push(
      'Максимальное отдаление должно быть числом в диапазоне от 0.1 до 100.0.',
    );
  }

  // Админское максимальное отдаление (Требование 11): число в диапазоне
  // [0.1, 100.0] — жёсткая проверка, как у публичного maxZoomOut (Требование
  // 11.5). Мягкий инвариант adminMaxZoomOut ≥ maxZoomOut (Требование 11.3) НЕ
  // блокирует сохранение: если админский предел строже публичного, он мягко
  // нормализуется вверх до maxZoomOut (администратор не может отдаляться ближе
  // обычного зрителя). Нормализация применяется только когда публичный предел
  // сам корректен; иначе значение поля берётся как есть.
  const { adminMaxZoomOut } = values;
  let effectiveAdminMaxZoomOut = adminMaxZoomOut;
  if (
    !Number.isFinite(adminMaxZoomOut) ||
    adminMaxZoomOut < 0.1 ||
    adminMaxZoomOut > 100
  ) {
    errors.push(
      'Максимальное отдаление (админ) должно быть числом в диапазоне от 0.1 до 100.0.',
    );
  } else if (
    Number.isFinite(maxZoomOut) &&
    maxZoomOut >= 0.1 &&
    maxZoomOut <= 100 &&
    adminMaxZoomOut < maxZoomOut
  ) {
    // Мягкая нормализация: поднимаем админский предел до публичного (Требование 11.3).
    effectiveAdminMaxZoomOut = maxZoomOut;
  }

  // Глубина прорисовки копий (Требование 12): целое число в диапазоне [1, 12].
  // При выходе за диапазон или нецелом значении сохранение отклоняется, а
  // значения формы сохраняются (функция лишь возвращает ошибки, не закрывая
  // модалку).
  const { copyDepth } = values;
  if (
    !Number.isInteger(copyDepth) ||
    copyDepth < MIN_COPY_DEPTH ||
    copyDepth > MAX_COPY_DEPTH
  ) {
    errors.push(
      `Глубина прорисовки копий должна быть целым числом от ${MIN_COPY_DEPTH} до ${MAX_COPY_DEPTH}.`,
    );
  }

  // Параметры геометрии: из пресета либо ручной ввод.
  const preset = MAP_PRESETS.find((p) => p.id === values.preset);
  const sides = preset ? preset.sides : values.sides;
  const vertexDegree = preset ? preset.vertexDegree : values.vertexDegree;
  let pairingWord = (values.pairingWord ?? '').trim();
  if (pairingWord === '' && preset) {
    pairingWord = preset.pairingWord;
  }

  let polygon: FundamentalPolygon | null = null;
  const sidesValid = Number.isInteger(sides) && sides >= 3;
  const degreeValid = Number.isInteger(vertexDegree) && vertexDegree >= 3;
  if (!sidesValid) {
    errors.push('Число сторон многоугольника должно быть целым и не меньше 3.');
  }
  if (!degreeValid) {
    errors.push('Степень вершины (q) должна быть целым числом и не меньше 3.');
  }

  // Автоопределение типа геометрии по {p,q} — единственный источник решения
  // (Требования 1.4, 1.5). Для автоопределённых евклидовых и сферических карт
  // добавляем русскоязычную пред-валидацию чётности и диапазона числа сторон,
  // чтобы пользователь видел понятные сообщения на русском (Требования 4.1, 4.2,
  // 8.1), а не англоязычную обёртку классификатора из validateMapGeometry.
  let euclideanSphericalPreError = false;
  if (sidesValid && degreeValid) {
    const detected = detectGeometryType(sides, vertexDegree);
    if (detected === 'euclidean' || detected === 'spherical') {
      if (sides % 2 !== 0) {
        errors.push(
          'Для евклидовых и сферических карт число сторон (p) должно быть чётным.',
        );
        euclideanSphericalPreError = true;
      }
      if (sides < MIN_GEOMETRY_SIDES || sides > MAX_GEOMETRY_SIDES) {
        errors.push(
          `Для евклидовых и сферических карт число сторон (p) должно быть в диапазоне ` +
            `[${MIN_GEOMETRY_SIDES}, ${MAX_GEOMETRY_SIDES}].`,
        );
        euclideanSphericalPreError = true;
      }
    }
  }

  // Построение фундаментального многоугольника общим построителем: ветвь
  // геометрии выбирается автоопределением, гиперболический путь неизменен
  // (Требования 2.3, 2.4, 2.5). Построение чистое и вход не мутирует.
  if (sidesValid && degreeValid) {
    try {
      polygon = buildRegularPolygon(sides, vertexDegree, 0, preset?.id);
    } catch (e) {
      errors.push(e instanceof Error ? e.message : 'Не удалось построить многоугольник.');
    }
  }

  const sidePairing: SidePairingSpec = { word: pairingWord };

  // Структурная валидация пары «многоугольник + склейка» — те же проверки, что и
  // серверный роут (Требования 4.3, 4.5). Итоговый тип берётся из успешного
  // результата validation.geometryType (Требование 1.5), а не повторным вызовом
  // классификатора. Если пред-валидация уже добавила русскую причину о
  // чётности/диапазоне, структурную валидацию пропускаем, чтобы наружу не вышла
  // англоязычная обёртка классификатора (дедупликация сообщений, Требование 8.1).
  let geometryType: GeometryType | null = null;
  if (polygon && !euclideanSphericalPreError) {
    const validation = validateMapGeometry(polygon, sidePairing);
    if (validation.valid) {
      geometryType = validation.geometryType;
    } else {
      errors.push(...validation.errors);
    }
  } else if (!polygon && pairingWord === '') {
    errors.push('Не задана схема склейки сторон.');
  }

  if (errors.length > 0 || !polygon || geometryType === null) {
    return { config: null, errors };
  }
  // Ссылка на превью: пустая строка означает отсутствие превью — поле не задаётся
  // в конфигурации, чтобы галерея показала статичный рендер (Требование 9.7).
  const previewImageUrl = (values.previewImageUrl ?? '').trim();
  return {
    config: {
      name,
      geometryType,
      polygon,
      sidePairing,
      scale,
      backgroundColor,
      visible: values.visible,
      // Множитель максимального отдаления (Требования 2.1, 9.1) — прочитан из
      // поля формы и провалидирован выше на диапазон [0.1, 100.0].
      maxZoomOut,
      // Админский множитель максимального отдаления (Требование 11) — прочитан из
      // отдельного поля формы, провалидирован на диапазон [0.1, 100.0] и мягко
      // нормализован до maxZoomOut, если был строже публичного предела
      // (`adminMaxZoomOut ≥ maxZoomOut`, Требование 11.3).
      adminMaxZoomOut: effectiveAdminMaxZoomOut,
      // Глубина прорисовки копий (Требование 12) — прочитана из поля формы и
      // провалидирована выше как целое в диапазоне [1, 12].
      copyDepth,
      // Обход «вершинным кольцом» и скрытие границы диска — булевы флажки формы,
      // фолбэк `false` (историческое поведение), если не заданы.
      vertexRingTraversal: values.vertexRingTraversal ?? false,
      hideDiskBoundary: values.hideDiskBoundary ?? false,
      ...(previewImageUrl !== '' ? { previewImageUrl } : {}),
    },
    errors: [],
  };
}

/**
 * Обратный маппинг: значения формы из существующей конфигурации карты для
 * заполнения полей при редактировании. Степень вершины `q` восстанавливается из
 * суммы углов (`angleSum = sides · 2π / q`).
 *
 * @param config конфигурация карты
 * @returns значения для полей формы
 */
export function mapConfigToFormValues(config: MapConfig): MapFormValues {
  const presetId =
    config.polygon.preset && MAP_PRESETS.some((p) => p.id === config.polygon.preset)
      ? config.polygon.preset
      : CUSTOM_PRESET_ID;
  const vertexDegree =
    config.polygon.angleSum > 0
      ? Math.round((config.polygon.sides * 2 * Math.PI) / config.polygon.angleSum)
      : config.polygon.sides;
  return {
    name: config.name,
    preset: presetId,
    sides: config.polygon.sides,
    vertexDegree,
    pairingWord: config.sidePairing.word,
    scale: config.scale,
    backgroundColor: config.backgroundColor,
    visible: config.visible,
    maxZoomOut: config.maxZoomOut ?? 1,
    adminMaxZoomOut: config.adminMaxZoomOut ?? 1,
    copyDepth: config.copyDepth ?? DEFAULT_COPY_DEPTH,
    vertexRingTraversal: config.vertexRingTraversal ?? false,
    hideDiskBoundary: config.hideDiskBoundary ?? false,
    previewImageUrl: config.previewImageUrl ?? '',
  };
}

/**
 * Разобрать ответ `GET /api/mapsadmin/maps` в массив конфигураций, нормализуя
 * поля, важные для отображения списка (Требование 1.2). Некорректные записи
 * отбрасываются, отсутствующие поля заменяются безопасными значениями.
 *
 * @param json разобранный JSON ответа
 * @returns нормализованный список конфигураций карт
 */
export function parseMapsList(json: unknown): MapConfig[] {
  if (!Array.isArray(json)) {
    return [];
  }
  const maps: MapConfig[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== 'string' || e.id === '') {
      continue;
    }
    const geometryType: GeometryType =
      e.geometryType === 'euclidean' || e.geometryType === 'spherical'
        ? e.geometryType
        : 'hyperbolic';
    maps.push({
      id: e.id,
      name: typeof e.name === 'string' ? e.name : 'Без названия',
      geometryType,
      polygon: (e.polygon as FundamentalPolygon) ?? {
        sides: 0,
        vertices: [],
        angleSum: 0,
      },
      sidePairing: (e.sidePairing as SidePairingSpec) ?? { word: '' },
      scale:
        typeof e.scale === 'number' && Number.isFinite(e.scale) ? e.scale : 0,
      backgroundColor:
        typeof e.backgroundColor === 'string' ? e.backgroundColor : '#000000',
      visible: e.visible !== false,
      maxZoomOut:
        typeof e.maxZoomOut === 'number' && Number.isFinite(e.maxZoomOut)
          ? e.maxZoomOut
          : 1,
      // Админский множитель максимального отдаления (Требование 11): фолбэк 1.0
      // для записей без столбца/поля (обратная совместимость, Требование 11.6).
      adminMaxZoomOut:
        typeof e.adminMaxZoomOut === 'number' && Number.isFinite(e.adminMaxZoomOut)
          ? e.adminMaxZoomOut
          : 1,
      // Глубина прорисовки копий (Требование 12): фолбэк 8 для записей без
      // столбца/поля (обратная совместимость, Требование 12.6).
      copyDepth:
        typeof e.copyDepth === 'number' && Number.isInteger(e.copyDepth) && e.copyDepth >= 1
          ? e.copyDepth
          : 8,
      // Обход «вершинным кольцом» и скрытие границы диска: фолбэк `false`
      // (историческое поведение) для записей без поля/столбца.
      vertexRingTraversal: e.vertexRingTraversal === true,
      hideDiskBoundary: e.hideDiskBoundary === true,
      previewImageUrl:
        typeof e.previewImageUrl === 'string' && e.previewImageUrl !== ''
          ? e.previewImageUrl
          : undefined,
      createdAt: typeof e.createdAt === 'string' ? e.createdAt : undefined,
      updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : undefined,
    });
  }
  return maps;
}

// ---------------------------------------------------------------------------
// Тонкий DOM-слой: страница админки карт
// ---------------------------------------------------------------------------

/** Опции конфигурации {@link MapsAdminPage}. */
export interface MapsAdminPageOptions {
  /** Базовый URL API (без завершающего слэша). По умолчанию пустая строка. */
  apiBase?: string;
  /** Функция загрузки (внедрение для тестов). По умолчанию глобальный `fetch`. */
  fetchFn?: typeof fetch;
  /** Хранилище учётных данных админ-сессии. По умолчанию `localStorage`. */
  authStorage?: CredentialStorage | null;
  /** Ключ в хранилище для заголовка Basic-авторизации. */
  authStorageKey?: string;
}

/** Небольшой помощник создания DOM-элемента с атрибутами и потомками. */
function createEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: Array<Node | string> = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'class') {
      el.className = value;
    } else {
      el.setAttribute(key, value);
    }
  }
  for (const child of children) {
    el.append(child);
  }
  return el;
}

/**
 * Страница админки карт: собирает DOM (список, модалки входа и формы), проверяет
 * авторизацию и обслуживает CRUD-операции над картами через `/api/mapsadmin`.
 *
 * Жизненный цикл:
 * 1. `new MapsAdminPage(container, options?)` — строит DOM и запускает
 *    {@link MapsAdminPage.init} (проверка сессии → показ списка либо модалки входа).
 * 2. вход → {@link MapsAdminPage.loadMaps} загружает и отображает список.
 * 3. создание/редактирование/удаление/переключение видимости — через модалку и
 *    соответствующие эндпоинты.
 */
export class MapsAdminPage {
  private readonly container: HTMLElement;
  private readonly apiBase: string;
  private readonly fetchFn: typeof fetch;
  private readonly session: AdminSession;

  // DOM-ссылки (создаются в buildDom, если доступен документ).
  private mainContent: HTMLElement | null = null;
  private loginModal: HTMLElement | null = null;
  private loginError: HTMLElement | null = null;
  private loginUsername: HTMLInputElement | null = null;
  private loginPassword: HTMLInputElement | null = null;
  private messageEl: HTMLElement | null = null;
  private tableBody: HTMLElement | null = null;

  // Модалка формы карты и её поля.
  private formModal: HTMLElement | null = null;
  private formTitle: HTMLElement | null = null;
  private formError: HTMLElement | null = null;
  private editingId: string | null = null;
  private fields: {
    name: HTMLInputElement;
    preset: HTMLSelectElement;
    sides: HTMLInputElement;
    vertexDegree: HTMLInputElement;
    pairingWord: HTMLInputElement;
    scale: HTMLInputElement;
    backgroundColor: HTMLInputElement;
    visible: HTMLInputElement;
    maxZoomOut: HTMLInputElement;
    adminMaxZoomOut: HTMLInputElement;
    copyDepth: HTMLInputElement;
    vertexRingTraversal: HTMLInputElement;
    hideDiskBoundary: HTMLInputElement;
    customRow: HTMLElement;
    previewImageUrl: HTMLInputElement;
    previewFile: HTMLInputElement;
    previewImg: HTMLImageElement;
  } | null = null;

  private messageTimer: ReturnType<typeof setTimeout> | null = null;

  /** Редактор легенды карты (модалки списка и формы элемента). */
  private legendEditor: LegendEditor | null = null;

  constructor(container: HTMLElement, options: MapsAdminPageOptions = {}) {
    this.container = container;
    this.apiBase = options.apiBase ?? '';
    this.fetchFn =
      options.fetchFn ??
      (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (undefined as never));
    this.session = new AdminSession({
      apiBase: this.apiBase,
      fetchFn: this.fetchFn,
      storage: options.authStorage,
      storageKey: options.authStorageKey,
      probePath: '/api/mapsadmin/maps',
    });

    if (typeof document !== 'undefined') {
      this.buildDom();
    }
  }

  /**
   * Инициализация: проверить действующую сессию (Требование 1.1). При наличии —
   * показать список карт; иначе — модалку входа.
   */
  async init(): Promise<void> {
    const authed = await this.session.probe();
    if (authed) {
      this.showMainContent();
      await this.loadMaps();
    } else {
      this.showLoginModal();
    }
  }

  /**
   * Загрузить и отобразить список всех карт (Требование 1.2): название, тип
   * геометрии, масштаб, статус видимости, действия.
   */
  async loadMaps(): Promise<void> {
    if (!this.tableBody) {
      return;
    }
    try {
      const response = await this.fetchFn(`${this.apiBase}/api/mapsadmin/maps`, {
        headers: this.session.authHeaders(),
      });
      if (response.status === 401 || response.status === 403) {
        this.showLoginModal();
        return;
      }
      if (!response.ok) {
        throw new Error(`код ${response.status}`);
      }
      const maps = parseMapsList(await response.json());
      this.renderMaps(maps);
    } catch (error) {
      this.showMessage(
        'Ошибка загрузки: ' + (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  /** Отрисовать строки таблицы карт. */
  private renderMaps(maps: MapConfig[]): void {
    const tbody = this.tableBody;
    if (!tbody) {
      return;
    }
    tbody.textContent = '';
    if (maps.length === 0) {
      const row = createEl('tr', {}, [createEl('td', { colspan: '5' }, ['Нет карт'])]);
      tbody.append(row);
      return;
    }
    for (const map of maps) {
      const badge = createEl(
        'span',
        { class: `badge ${map.visible ? 'badge-green' : 'badge-gray'}` },
        [map.visible ? 'Видимая' : 'Скрытая'],
      );

      const editBtn = createEl('button', { class: 'btn btn-sm', title: 'Редактировать' }, ['✏️']);
      editBtn.addEventListener('click', () => void this.openEditModal(map.id));
      const legendBtn = createEl('button', { class: 'btn btn-sm', title: 'Легенда' }, ['📋']);
      legendBtn.addEventListener('click', () => void this.openLegendEditor(map.id, map.name));
      const toggleBtn = createEl('button', { class: 'btn btn-sm', title: 'Видимость' }, [
        map.visible ? '👁️' : '🚫',
      ]);
      toggleBtn.addEventListener('click', () => void this.toggleVisibility(map.id));
      const viewLink = createEl('a', {
        class: 'btn btn-sm',
        href: `/map/${encodeURIComponent(map.id)}`,
        title: 'Открыть',
        target: '_blank',
      }, ['🗺️']);
      const deleteBtn = createEl('button', { class: 'btn btn-sm btn-danger', title: 'Удалить' }, ['🗑️']);
      deleteBtn.addEventListener('click', () => void this.deleteMap(map.id, map.name));

      const actions = createEl('td', { class: 'actions' }, [
        editBtn,
        legendBtn,
        toggleBtn,
        viewLink,
        deleteBtn,
      ]);

      const row = createEl('tr', {}, [
        createEl('td', {}, [map.name]),
        createEl('td', {}, [formatGeometryType(map.geometryType)]),
        createEl('td', {}, [String(map.scale)]),
        createEl('td', {}, [badge]),
        actions,
      ]);
      tbody.append(row);
    }
  }

  // -------------------------------------------------------------------------
  // Авторизация (Требование 1.1)
  // -------------------------------------------------------------------------

  /** Обработать отправку формы входа. */
  private async handleLogin(): Promise<void> {
    const username = this.loginUsername?.value ?? '';
    const password = this.loginPassword?.value ?? '';
    if (this.loginError) {
      this.loginError.textContent = '';
    }
    const ok = await this.session.login(username, password);
    if (ok) {
      this.hideLoginModal();
      this.showMainContent();
      await this.loadMaps();
    } else if (this.loginError) {
      this.loginError.textContent = 'Неверный логин или пароль';
    }
  }

  private showLoginModal(): void {
    this.loginModal?.classList.add('active');
    if (this.mainContent) {
      this.mainContent.style.display = 'none';
    }
  }

  private hideLoginModal(): void {
    this.loginModal?.classList.remove('active');
  }

  private showMainContent(): void {
    this.hideLoginModal();
    if (this.mainContent) {
      this.mainContent.style.display = 'block';
    }
  }

  // -------------------------------------------------------------------------
  // Создание / редактирование (Требования 1.3, 1.4, 1.8, 2.1)
  // -------------------------------------------------------------------------

  /** Открыть модалку создания новой карты. */
  openCreateModal(): void {
    this.editingId = null;
    if (this.formTitle) {
      this.formTitle.textContent = 'Создать карту';
    }
    this.applyFormValues({
      name: '',
      preset: MAP_PRESETS[0].id,
      sides: MAP_PRESETS[0].sides,
      vertexDegree: MAP_PRESETS[0].vertexDegree,
      pairingWord: MAP_PRESETS[0].pairingWord,
      scale: 4,
      backgroundColor: '#101018',
      visible: true,
      maxZoomOut: 1,
      adminMaxZoomOut: 1,
      copyDepth: DEFAULT_COPY_DEPTH,
      vertexRingTraversal: false,
      hideDiskBoundary: false,
      previewImageUrl: '',
    });
    this.openFormModal();
  }

  /** Загрузить карту и открыть модалку редактирования (Требование 1.4). */
  async openEditModal(id: string): Promise<void> {
    try {
      const response = await this.fetchFn(
        `${this.apiBase}/api/mapsadmin/maps`,
        { headers: this.session.authHeaders() },
      );
      if (!response.ok) {
        throw new Error(`код ${response.status}`);
      }
      const maps = parseMapsList(await response.json());
      const map = maps.find((m) => m.id === id);
      if (!map) {
        this.showMessage('Карта не найдена', 'error');
        return;
      }
      this.editingId = id;
      if (this.formTitle) {
        this.formTitle.textContent = 'Редактировать карту';
      }
      this.applyFormValues(mapConfigToFormValues(map));
      this.openFormModal();
    } catch (error) {
      this.showMessage(
        'Ошибка: ' + (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  /** Считать значения формы из полей DOM. */
  private readFormValues(): MapFormValues {
    const f = this.fields;
    if (!f) {
      throw new Error('Форма не инициализирована');
    }
    return {
      name: f.name.value,
      preset: f.preset.value,
      sides: parseInt(f.sides.value, 10),
      vertexDegree: parseInt(f.vertexDegree.value, 10),
      pairingWord: f.pairingWord.value,
      scale: parseInt(f.scale.value, 10),
      backgroundColor: f.backgroundColor.value,
      visible: f.visible.checked,
      maxZoomOut: parseFloat(f.maxZoomOut.value),
      adminMaxZoomOut: parseFloat(f.adminMaxZoomOut.value),
      copyDepth: parseInt(f.copyDepth.value, 10),
      vertexRingTraversal: f.vertexRingTraversal.checked,
      hideDiskBoundary: f.hideDiskBoundary.checked,
      previewImageUrl: f.previewImageUrl.value,
    };
  }

  /** Заполнить поля формы значениями. */
  private applyFormValues(values: MapFormValues): void {
    const f = this.fields;
    if (!f) {
      return;
    }
    f.name.value = values.name;
    f.preset.value = MAP_PRESETS.some((p) => p.id === values.preset)
      ? values.preset
      : CUSTOM_PRESET_ID;
    f.sides.value = String(values.sides);
    f.vertexDegree.value = String(values.vertexDegree);
    f.pairingWord.value = values.pairingWord;
    f.scale.value = String(values.scale);
    f.backgroundColor.value = isHexColor(values.backgroundColor)
      ? values.backgroundColor
      : '#101018';
    f.visible.checked = values.visible;
    f.maxZoomOut.value = String(values.maxZoomOut);
    f.adminMaxZoomOut.value = String(values.adminMaxZoomOut);
    f.copyDepth.value = String(values.copyDepth);
    f.vertexRingTraversal.checked = values.vertexRingTraversal ?? false;
    f.hideDiskBoundary.checked = values.hideDiskBoundary ?? false;
    f.previewImageUrl.value = values.previewImageUrl ?? '';
    f.previewFile.value = '';
    this.syncPreviewImg();
    if (this.formError) {
      this.formError.textContent = '';
    }
    this.syncCustomRow();
  }

  /**
   * Синхронизировать миниатюру превью с текущим значением ссылки: при непустой
   * ссылке показать изображение, иначе скрыть (Требования 9.1, 9.7).
   */
  private syncPreviewImg(): void {
    const f = this.fields;
    if (!f) {
      return;
    }
    const url = f.previewImageUrl.value.trim();
    if (url !== '') {
      f.previewImg.src = url;
      f.previewImg.style.display = '';
    } else {
      f.previewImg.removeAttribute('src');
      f.previewImg.style.display = 'none';
    }
  }

  /** Показать/скрыть поля ручного ввода `{p,q}` в зависимости от выбора пресета. */
  private syncCustomRow(): void {
    const f = this.fields;
    if (!f) {
      return;
    }
    const isCustom = !MAP_PRESETS.some((p) => p.id === f.preset.value);
    f.customRow.style.display = isCustom ? '' : 'none';
    if (!isCustom) {
      const preset = MAP_PRESETS.find((p) => p.id === f.preset.value);
      if (preset) {
        f.sides.value = String(preset.sides);
        f.vertexDegree.value = String(preset.vertexDegree);
        if (f.pairingWord.value.trim() === '') {
          f.pairingWord.value = preset.pairingWord;
        }
      }
    }
  }

  private openFormModal(): void {
    this.formModal?.classList.add('active');
  }

  private closeFormModal(): void {
    this.formModal?.classList.remove('active');
  }

  /**
   * Загрузить изображение на сервер (`POST /api/mapsadmin/upload/image`) через
   * существующий механизм multer и вернуть URL сохранённого файла (Требование 9.1,
   * 9.2). Тип (PNG/JPEG/WebP) и размер (≤ 5 МБ) валидируются сервером; при ошибке
   * пробрасывается исключение с сообщением о причине (Требование 9.3).
   *
   * @param file файл изображения превью
   * @returns URL загруженного изображения
   */
  async uploadImage(file: File): Promise<string> {
    const form = new FormData();
    form.append('image', file);
    const response = await this.fetchFn(`${this.apiBase}/api/mapsadmin/upload/image`, {
      method: 'POST',
      // Content-Type для multipart/form-data проставляет браузер (с boundary).
      headers: { ...this.session.authHeaders() },
      body: form,
    });
    if (response.status === 401 || response.status === 403) {
      this.showLoginModal();
      throw new Error('Сессия истекла');
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error || `Ошибка загрузки (код ${response.status})`);
    }
    const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    const url = json && typeof json.url === 'string' && json.url !== '' ? json.url : null;
    if (!url) {
      throw new Error('Сервер не вернул ссылку на изображение');
    }
    return url;
  }

  /**
   * Обработать выбор файла превью: загрузить его на сервер и заполнить ссылку
   * с миниатюрой (Требование 9.1). Ошибка загрузки показывается в форме, поле
   * ссылки не изменяется.
   */
  private async handlePreviewFile(): Promise<void> {
    const f = this.fields;
    if (!f || !f.previewFile.files || f.previewFile.files.length === 0) {
      return;
    }
    const file = f.previewFile.files[0];
    try {
      const url = await this.uploadImage(file);
      f.previewImageUrl.value = url;
      this.syncPreviewImg();
    } catch (error) {
      if (this.formError) {
        this.formError.textContent =
          'Не удалось загрузить превью: ' +
          (error instanceof Error ? error.message : String(error));
      }
    } finally {
      f.previewFile.value = '';
    }
  }

  /**
   * Сохранить карту: собрать конфигурацию из формы ({@link buildMapConfigFromForm}),
   * при ошибках показать их; иначе — `POST` (создание, Требование 1.3) или `PUT`
   * (редактирование, Требование 1.4).
   */
  private async saveMap(): Promise<void> {
    let values: MapFormValues;
    try {
      values = this.readFormValues();
    } catch {
      return;
    }
    const { config, errors } = buildMapConfigFromForm(values);
    if (!config) {
      if (this.formError) {
        this.formError.textContent = errors.join(' ');
      }
      return;
    }

    const isEdit = this.editingId !== null;
    const url = isEdit
      ? `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(this.editingId as string)}`
      : `${this.apiBase}/api/mapsadmin/maps`;
    try {
      const response = await this.fetchFn(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...this.session.authHeaders() },
        body: JSON.stringify(config),
      });
      if (response.status === 401 || response.status === 403) {
        this.showLoginModal();
        return;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { error?: string; details?: string[] }
          | null;
        const detail = body?.details?.join(', ') || body?.error || `код ${response.status}`;
        if (this.formError) {
          this.formError.textContent = detail;
        }
        return;
      }
      this.closeFormModal();
      this.showMessage(isEdit ? 'Карта обновлена' : 'Карта создана');
      await this.loadMaps();
    } catch (error) {
      if (this.formError) {
        this.formError.textContent =
          error instanceof Error ? error.message : String(error);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Удаление и видимость (Требования 1.5, 1.6)
  // -------------------------------------------------------------------------

  /** Удалить карту после подтверждения (Требование 1.5). */
  async deleteMap(id: string, name: string): Promise<void> {
    const confirmFn = typeof window !== 'undefined' ? window.confirm : () => true;
    if (!confirmFn(`Удалить карту «${name}» со всеми слоями, метками и легендой?`)) {
      return;
    }
    try {
      const response = await this.fetchFn(
        `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(id)}`,
        { method: 'DELETE', headers: this.session.authHeaders() },
      );
      if (response.status === 401 || response.status === 403) {
        this.showLoginModal();
        return;
      }
      if (!response.ok && response.status !== 204) {
        throw new Error(`код ${response.status}`);
      }
      this.showMessage('Карта удалена');
      await this.loadMaps();
    } catch (error) {
      this.showMessage(
        'Ошибка: ' + (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  /** Переключить видимость карты (Требование 1.6). */
  async toggleVisibility(id: string): Promise<void> {
    try {
      const response = await this.fetchFn(
        `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(id)}/toggle-visibility`,
        { method: 'POST', headers: this.session.authHeaders() },
      );
      if (response.status === 401 || response.status === 403) {
        this.showLoginModal();
        return;
      }
      if (!response.ok) {
        throw new Error(`код ${response.status}`);
      }
      await this.loadMaps();
    } catch (error) {
      this.showMessage(
        'Ошибка: ' + (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  // -------------------------------------------------------------------------
  // Легенда (Требования 6.1, 6.2, 6.3, 6.4, 6.7)
  // -------------------------------------------------------------------------

  /** Открыть редактор легенды выбранной карты. */
  async openLegendEditor(id: string, name: string): Promise<void> {
    if (!this.legendEditor) {
      return;
    }
    await this.legendEditor.open(id, name);
  }

  // -------------------------------------------------------------------------
  // Сообщения
  // -------------------------------------------------------------------------

  private showMessage(text: string, type: 'success' | 'error' = 'success'): void {
    if (!this.messageEl) {
      return;
    }
    this.messageEl.textContent = '';
    const box = createEl('div', { class: `message message-${type}` }, [text]);
    this.messageEl.append(box);
    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
    }
    this.messageTimer = setTimeout(() => {
      if (this.messageEl) {
        this.messageEl.textContent = '';
      }
    }, 5000);
  }

  // -------------------------------------------------------------------------
  // Построение DOM (тонкий слой)
  // -------------------------------------------------------------------------

  /** Построить DOM-структуру: заголовок, список, модалки входа и формы. */
  private buildDom(): void {
    this.container.textContent = '';
    this.container.append(this.buildMainContent());
    this.container.append(this.buildFormModal());
    this.container.append(this.buildLoginModal());

    // Редактор легенды строит собственные модалки в контейнере страницы.
    this.legendEditor = new LegendEditor(this.container, {
      apiBase: this.apiBase,
      fetchFn: this.fetchFn,
      authHeaders: () => this.session.authHeaders(),
      onMessage: (text, type) => this.showMessage(text, type),
      onUnauthorized: () => this.showLoginModal(),
    });
  }

  private buildMainContent(): HTMLElement {
    const createBtn = createEl('button', { class: 'btn btn-primary', type: 'button' }, [
      '+ Создать карту',
    ]);
    createBtn.addEventListener('click', () => this.openCreateModal());

    const header = createEl('div', { class: 'header' }, [
      createEl('h1', {}, ['Админка карт']),
      createEl('div', {}, [
        createEl('a', { class: 'btn', href: '/' }, ['← На сайт']),
        createBtn,
      ]),
    ]);

    this.messageEl = createEl('div', { id: 'message' });

    this.tableBody = createEl('tbody', {}, [
      createEl('tr', {}, [createEl('td', { colspan: '5' }, ['Загрузка...'])]),
    ]);
    const table = createEl('table', {}, [
      createEl('thead', {}, [
        createEl('tr', {}, [
          createEl('th', {}, ['Название']),
          createEl('th', {}, ['Геометрия']),
          createEl('th', {}, ['Масштаб']),
          createEl('th', {}, ['Статус']),
          createEl('th', {}, ['Действия']),
        ]),
      ]),
      this.tableBody,
    ]);

    const main = createEl('div', { class: 'maps-admin' }, [
      header,
      this.messageEl,
      createEl('h2', {}, ['Карты']),
      table,
    ]);
    main.style.display = 'none';
    this.mainContent = main;
    return main;
  }

  private buildFormModal(): HTMLElement {
    const name = createEl('input', { type: 'text', required: 'true' }) as HTMLInputElement;

    const preset = createEl('select', {}) as HTMLSelectElement;
    for (const p of MAP_PRESETS) {
      preset.append(createEl('option', { value: p.id }, [p.label]));
    }
    preset.append(
      createEl('option', { value: CUSTOM_PRESET_ID }, ['Произвольный многоугольник {p,q}']),
    );
    preset.addEventListener('change', () => this.syncCustomRow());

    const sides = createEl('input', {
      type: 'number',
      min: '3',
      max: '64',
      value: '8',
    }) as HTMLInputElement;
    const vertexDegree = createEl('input', {
      type: 'number',
      min: '3',
      max: '64',
      value: '8',
    }) as HTMLInputElement;
    const customRow = createEl('div', { class: 'form-row' }, [
      this.buildField('Число сторон (p)', sides),
      this.buildField('Степень вершины (q)', vertexDegree),
    ]);

    const pairingWord = createEl('input', {
      type: 'text',
      value: 'abABcdCD',
    }) as HTMLInputElement;

    const scale = createEl('input', {
      type: 'number',
      min: '1',
      max: String(MAX_MAP_SCALE),
      value: '4',
    }) as HTMLInputElement;
    const backgroundColor = createEl('input', {
      type: 'color',
      value: '#101018',
    }) as HTMLInputElement;

    const visible = createEl('input', { type: 'checkbox' }) as HTMLInputElement;
    visible.checked = true;

    // Поле максимального отдаления (Требования 2.1, 9.1): положительное число в
    // диапазоне [0.1, 100.0] с шагом 0.1, значение по умолчанию 1.0 (нижняя
    // граница расширена до 0.1 — Требование 9.1).
    const maxZoomOut = createEl('input', {
      type: 'number',
      min: '0.1',
      max: '100',
      step: '0.1',
      value: '1',
    }) as HTMLInputElement;

    // Поле админского максимального отдаления (Требование 11): положительное
    // число в диапазоне [0.1, 100.0] с шагом 0.1, значение по умолчанию 1.0.
    // Применяется только для авторизованного администратора и не должно быть
    // строже публичного предела (Требование 11.3).
    const adminMaxZoomOut = createEl('input', {
      type: 'number',
      min: '0.1',
      max: '100',
      step: '0.1',
      value: '1',
    }) as HTMLInputElement;

    // Поле глубины прорисовки копий (Требование 12): целое число в диапазоне
    // [1, 12] с шагом 1, значение по умолчанию 8 (текущий neighborDepth
    // менеджера LOD, Требование 12.6).
    const copyDepth = createEl('input', {
      type: 'number',
      min: String(MIN_COPY_DEPTH),
      max: String(MAX_COPY_DEPTH),
      step: '1',
      value: String(DEFAULT_COPY_DEPTH),
    }) as HTMLInputElement;

    // Флажок обхода «вершинным кольцом»: вокруг каждой вершины прорисовывается
    // полный цикл примыкающих копий (в т.ч. с противоположной стороны области),
    // которые при обходе по глубине пропадают. По умолчанию выключен.
    const vertexRingTraversal = createEl('input', {
      type: 'checkbox',
    }) as HTMLInputElement;

    // Флажок скрытия границы окружности Пуанкаре. По умолчанию выключен
    // (граница видима).
    const hideDiskBoundary = createEl('input', {
      type: 'checkbox',
    }) as HTMLInputElement;

    // Контрол превью (Требование 9.1): ввод ссылки ИЛИ загрузка файла, миниатюра
    // и кнопка очистки (удаление превью → previewImageUrl не задаётся, Требование 9.7).
    const previewImg = createEl('img', {
      class: 'map-preview-thumb',
      alt: 'Превью карты',
    }) as HTMLImageElement;
    previewImg.style.display = 'none';
    previewImg.style.maxWidth = '160px';

    const previewImageUrl = createEl('input', {
      type: 'text',
      placeholder: 'https://… или загрузите файл ниже',
    }) as HTMLInputElement;
    previewImageUrl.addEventListener('input', () => this.syncPreviewImg());

    const previewFile = createEl('input', {
      type: 'file',
      accept: 'image/png,image/jpeg,image/webp',
    }) as HTMLInputElement;
    previewFile.addEventListener('change', () => void this.handlePreviewFile());

    const clearPreviewBtn = createEl(
      'button',
      { class: 'btn btn-sm', type: 'button' },
      ['Удалить превью'],
    );
    clearPreviewBtn.addEventListener('click', () => {
      const f = this.fields;
      if (!f) {
        return;
      }
      f.previewImageUrl.value = '';
      f.previewFile.value = '';
      this.syncPreviewImg();
    });

    const previewControl = createEl('div', { class: 'map-preview-control' }, [
      previewImg,
      previewImageUrl,
      previewFile,
      clearPreviewBtn,
    ]);

    this.fields = {
      name,
      preset,
      sides,
      vertexDegree,
      pairingWord,
      scale,
      backgroundColor,
      visible,
      maxZoomOut,
      adminMaxZoomOut,
      copyDepth,
      vertexRingTraversal,
      hideDiskBoundary,
      customRow,
      previewImageUrl,
      previewFile,
      previewImg,
    };

    this.formError = createEl('div', { class: 'modal-error' });
    this.formTitle = createEl('h2', {}, ['Создать карту']);

    const closeBtn = createEl('button', { class: 'close-btn', type: 'button' }, ['×']);
    closeBtn.addEventListener('click', () => this.closeFormModal());

    const form = createEl('form', {}, [
      this.buildField('Название', name),
      this.buildField('Фундаментальный многоугольник', preset),
      customRow,
      this.buildField('Схема склейки сторон', pairingWord),
      createEl('div', { class: 'form-row' }, [
        this.buildField('Масштаб (уровни LOD)', scale),
        this.buildField('Цвет фона', backgroundColor),
      ]),
      this.buildField('Максимальное отдаление (0.1–100.0)', maxZoomOut),
      this.buildField('Максимальное отдаление (админ) (0.1–100.0)', adminMaxZoomOut),
      this.buildField('Глубина прорисовки копий (1–12)', copyDepth),
      createEl('label', { class: 'checkbox-label' }, [visible, ' Видимая в галерее']),
      createEl('label', { class: 'checkbox-label' }, [
        vertexRingTraversal,
        ' Круговой обход копий вокруг вершины (вершинное кольцо)',
      ]),
      createEl('label', { class: 'checkbox-label' }, [
        hideDiskBoundary,
        ' Скрыть границу окружности Пуанкаре',
      ]),
      this.buildField('Превью в галерее', previewControl),
      this.formError,
      createEl('button', { class: 'btn btn-primary', type: 'submit' }, ['Сохранить']),
    ]) as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.saveMap();
    });

    const content = createEl('div', { class: 'modal-content' }, [
      createEl('div', { class: 'modal-header' }, [this.formTitle, closeBtn]),
      form,
    ]);
    const modal = createEl('div', { class: 'modal', id: 'formModal' }, [content]);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeFormModal();
      }
    });
    this.formModal = modal;
    return modal;
  }

  private buildField(labelText: string, control: HTMLElement): HTMLElement {
    return createEl('div', { class: 'form-group' }, [
      createEl('label', {}, [labelText]),
      control,
    ]);
  }

  private buildLoginModal(): HTMLElement {
    this.loginUsername = createEl('input', {
      type: 'text',
      value: 'admin',
      autocomplete: 'username',
      required: 'true',
    }) as HTMLInputElement;
    this.loginPassword = createEl('input', {
      type: 'password',
      autocomplete: 'current-password',
      required: 'true',
    }) as HTMLInputElement;
    this.loginError = createEl('div', { class: 'modal-error' });

    const form = createEl('form', {}, [
      this.buildField('Логин', this.loginUsername),
      this.buildField('Пароль', this.loginPassword),
      this.loginError,
      createEl('button', { class: 'btn btn-primary', type: 'submit' }, ['Войти']),
    ]) as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.handleLogin();
    });

    const content = createEl('div', { class: 'modal-content', style: 'max-width: 400px;' }, [
      createEl('h2', {}, ['Вход в админку карт']),
      form,
    ]);
    const modal = createEl('div', { class: 'modal', id: 'loginModal' }, [content]);
    this.loginModal = modal;
    return modal;
  }
}
